import React, { useState, useEffect } from "react";
import {
  Search,
  RotateCw,
  SlidersHorizontal,
  ChevronDown,
  Eraser,
  Calendar,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getCertificateTypes } from "../../../../api/elearning/certificates/certificates";
import { getSubjectsLookup } from "../../../../api/elearning/subjects/subjectInfo";
import CertificateToolbar from "./CertificateToolbar";

const CertificateFilter = ({
  filters,
  setFilters,
  onRefresh,
  onIssue = () => {},
  showDirectIssueButton = false,
}) => {
  const [advanced, setAdvanced] = useState(false);
  const [types, setTypes] = useState([]);
  const [subjects, setSubjects] = useState([]);

  useEffect(() => {
    getCertificateTypes().then((d) => setTypes(d.types || [])).catch(() => setTypes([]));
    getSubjectsLookup({ status: "active" })
      .then((r) => setSubjects(Array.isArray(r) ? r : r?.records || []))
      .catch(() => setSubjects([]));
  }, []);

  const isFiltering =
    filters?.recipient_type !== "all" ||
    filters?.cert_type !== "all" ||
    filters?.subject !== "all" ||
    Boolean(String(filters?.grade || "").trim()) ||
    (filters?.search && filters.search !== "");

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      recipient_type: "all",
      cert_type: "all",
      subject: "all",
      grade: "",
      search: "",
      year: new Date().getFullYear().toString(),
    });
  };

  const ActionButton = ({ icon: Icon, onClick, title, active }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all duration-200 ${
        active
          ? "bg-red-50 border-red-200 text-red-600 shadow-sm"
          : "bg-white border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500"
      }`}
    >
      <Icon size={18} />
    </button>
  );

  const SelectBox = ({ label, value, options, onChange, placeholder }) => (
    <div className="flex flex-col gap-1.5 w-full text-left">
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
        {label}
      </label>
      <div className="relative group">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-10 rounded-2xl text-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all cursor-pointer font-medium shadow-sm"
        >
          <option value="all">{placeholder}</option>
          {options.map((opt) => (
            <option key={opt.id || opt} value={opt.id || opt}>
              {opt.name || opt}
            </option>
          ))}
        </select>
        <ChevronDown
          size={16}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-hover:text-red-500 transition-colors"
        />
      </div>
    </div>
  );

  return (
    <div className="bg-white p-4 sm:p-5 rounded-2xl border border-gray-100 shadow-sm mb-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between xl:gap-6">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 flex-1 min-w-0">
          <div className="relative w-full min-w-0 sm:max-w-sm flex-1 group">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
              size={18}
            />
            <input
              type="text"
              placeholder="Tìm tên người nhận, mã số bằng..."
              value={filters?.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              className="w-full bg-gray-50 border-transparent border-2 focus:bg-white focus:border-red-500 pl-11 pr-4 py-2.5 rounded-2xl outline-none transition-all font-medium text-gray-700"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <ActionButton
              icon={RotateCw}
              onClick={() => (onRefresh ? onRefresh() : setFilters({ ...filters }))}
              title="Làm mới"
            />
            <ActionButton
              icon={SlidersHorizontal}
              onClick={() => setAdvanced(!advanced)}
              active={advanced}
              title="Lọc nâng cao"
            />
            {isFiltering && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs font-bold text-red-500 hover:text-red-700 flex items-center gap-1 uppercase tracking-wider transition-all"
              >
                <Eraser size={14} /> Xóa lọc
              </button>
            )}
          </div>
        </div>

        <CertificateToolbar
          variant="issued"
          showTemplates={false}
          onIssue={onIssue}
          showDirectIssueButton={showDirectIssueButton}
          className="w-full xl:w-auto xl:justify-end border-t border-gray-100 pt-4 xl:border-t-0 xl:pt-0"
        />
      </div>

      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mt-6 pt-6 border-t border-gray-100">
              <SelectBox
                label="Đối tượng"
                value={filters?.recipient_type}
                options={[
                  { id: "student", name: "Học viên" },
                  { id: "teacher", name: "Giáo viên" },
                ]}
                onChange={(v) => handleFilterChange("recipient_type", v)}
                placeholder="Tất cả"
              />
              <SelectBox
                label="Loại chứng nhận"
                value={filters?.cert_type}
                options={types}
                onChange={(v) => handleFilterChange("cert_type", v)}
                placeholder="Tất cả loại"
              />
              <SelectBox
                label="Môn học"
                value={filters?.subject}
                options={subjects}
                onChange={(v) => handleFilterChange("subject", v)}
                placeholder="Tất cả môn"
              />
              <div className="flex flex-col gap-1.5 w-full text-left">
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                  Kết quả
                </label>
                <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-2xl px-4 py-2.5 shadow-sm">
                  <Search size={16} className="text-gray-400" />
                  <input
                    type="text"
                    value={filters?.grade || ""}
                    onChange={(e) => handleFilterChange("grade", e.target.value)}
                    placeholder="Nhập kết quả cần lọc"
                    className="bg-transparent text-sm font-medium text-gray-700 outline-none w-full"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1.5 w-full">
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                  Năm cấp bằng
                </label>
                <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-2xl px-4 py-2.5 shadow-sm">
                  <Calendar size={16} className="text-gray-400" />
                  <input
                    type="number"
                    min="2020"
                    max="2030"
                    value={filters?.year || new Date().getFullYear()}
                    onChange={(e) => handleFilterChange("year", e.target.value)}
                    className="bg-transparent text-sm font-bold text-gray-700 outline-none w-full"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CertificateFilter;
